<template>
  <div class="shared-view-embed">
    <div
      class="shared-view-frame"
      :class="{ 'demo-active': demoActive }"
      ref="frame"
    >
      <iframe
        ref="viewIframe"
        :src="viewUrl"
        :title="`Saved ${viewLabel} preview`"
        loading="lazy"
        :tabindex="!demoActive ? -1 : 0"
        :inert="!demoActive"
      ></iframe>
      <DemoInteractionGate
        :active="demoActive"
        label="Explore this View"
        :open-href="openUrl"
        @activate="activateDemo"
        @deactivate="deactivateDemo"
      />
    </div>
  </div>
</template>

<script>
import DemoInteractionGate from '../ui/DemoInteractionGate.vue';

export default {
  name: 'EmbeddedSharedView',
  components: { DemoInteractionGate },
  props: {
    shareId: {
      type: String,
      required: true,
    },
    viewLabel: {
      type: String,
      default: 'View',
    },
  },
  data() {
    return {
      demoActive: false,
    };
  },
  computed: {
    viewUrl() {
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      return `${origin}${window.location.pathname}#/view/${this.shareId}?embedded=1&preview=1`;
    },
    openUrl() {
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      return `${origin}${window.location.pathname}#/view/${this.shareId}?presentation=article&temporaryPresentation=1`;
    },
  },
  mounted() {
    this._onResize = () => this.scaleFrame();
    window.addEventListener('resize', this._onResize);
    this.$nextTick(() => this.scaleFrame());
  },
  unmounted() {
    if (this._onResize) window.removeEventListener('resize', this._onResize);
  },
  methods: {
    activateDemo() {
      this.demoActive = true;
      this.$nextTick(() => this.$refs.viewIframe?.focus({ preventScroll: true }));
    },
    deactivateDemo() {
      this.demoActive = false;
    },
    scaleFrame() {
      const frame = this.$refs.frame;
      const iframe = this.$refs.viewIframe;
      if (!frame || !iframe) return;
      // The View page lays out at this width; the design width sits just
      // outside the content column so the preview shows the page nearly
      // edge to edge instead of shrinking a wide desktop layout.
      const DESIGN_W = 640;
      const DESIGN_H = 650;
      const cw = frame.clientWidth;
      if (cw >= 460) {
        const scale = cw / DESIGN_W;
        iframe.style.width = `${DESIGN_W}px`;
        iframe.style.height = `${DESIGN_H}px`;
        iframe.style.transform = `scale(${scale})`;
        frame.style.height = `${Math.round(DESIGN_H * scale)}px`;
      } else {
        iframe.style.width = '100%';
        iframe.style.height = '';
        iframe.style.transform = 'none';
        frame.style.height = '';
      }
    },
  },
};
</script>

<style scoped>
.shared-view-embed {
  --frame-open-line: var(--story-line, rgba(0, 0, 0, 0.14));
  --frame-open-muted: var(--story-muted, #777777);
  --frame-open-ink: var(--story-ink, #343434);
  --frame-open-hover: rgba(0, 0, 0, 0.055);
  --frame-open-press: rgba(0, 0, 0, 0.09);
  margin: 1.3rem 0 0;
  border: 1px solid var(--story-line, rgba(0, 0, 0, 0.14));
  border-radius: 12px;
  overflow: hidden;
  background: var(--story-wash, rgba(0, 0, 0, 0.028));
}

.shared-view-frame {
  position: relative;
  isolation: isolate;
  width: 100%;
  overflow: hidden;
}

.shared-view-frame iframe {
  display: block;
  width: 100%;
  height: 620px;
  border: 0;
  transform-origin: top left;
}

:global(body.dark-mode .shared-view-embed) {
  --frame-open-hover: rgba(255, 255, 255, 0.055);
  --frame-open-press: rgba(255, 255, 255, 0.09);
}

.shared-view-frame iframe { pointer-events: none; }
.shared-view-frame.demo-active iframe { pointer-events: auto; }

@media (max-width: 700px) {
  .shared-view-frame iframe { height: 460px; }
}
</style>
